/**
 * Archivo Adjunto Viewer Dialog
 * Dialog for viewing file attachment details
 */

import { useTranslation } from "react-i18next";
import { Download, ExternalLink, FileText } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { useArchivoAdjunto } from "@/hooks/use-archivos-adjuntos";

interface ArchivoAdjuntoViewerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  archivoId: number;
}

export function ArchivoAdjuntoViewerDialog({
  open,
  onOpenChange,
  archivoId,
}: ArchivoAdjuntoViewerDialogProps) {
  const { t } = useTranslation();
  const { data: archivo, isLoading } = useArchivoAdjunto(archivoId);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("es-HN", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleDownload = () => {
    if (archivo?.url) {
      window.open(archivo.url, "_blank");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("archivosAdjuntos.viewTitle")}</DialogTitle>
          <DialogDescription>{archivo?.nombre_archivo || t("common.loading")}</DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <p className="text-muted-foreground">{t("common.loading")}</p>
          </div>
        ) : archivo ? (
          <div className="space-y-4">
            {/* Preview */}
            {archivo.es_imagen && archivo.url && (
              <div className="rounded-lg overflow-hidden border bg-muted">
                <img
                  src={archivo.url}
                  alt={archivo.nombre_archivo}
                  className="w-full max-h-[400px] object-contain"
                />
              </div>
            )}

            {archivo.es_pdf && archivo.url && (
              <div className="rounded-lg border bg-muted p-8 flex flex-col items-center justify-center">
                <FileText className="h-16 w-16 text-red-500 mb-4" />
                <p className="text-sm text-muted-foreground mb-4">
                  {t("archivosAdjuntos.pdfPreview")}
                </p>
                <Button onClick={handleDownload}>
                  <ExternalLink className="mr-2 h-4 w-4" />
                  {t("archivosAdjuntos.openPdf")}
                </Button>
              </div>
            )}

            {!archivo.es_imagen && !archivo.es_pdf && archivo.url && (
              <div className="rounded-lg border bg-muted p-8 flex flex-col items-center justify-center">
                <FileText className="h-16 w-16 text-gray-500 mb-4" />
                <p className="text-sm text-muted-foreground mb-4">
                  {t("archivosAdjuntos.filePreview")}
                </p>
                <Button onClick={handleDownload}>
                  <Download className="mr-2 h-4 w-4" />
                  {t("archivosAdjuntos.download")}
                </Button>
              </div>
            )}

            <Separator />

            {/* Details */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  {t("archivosAdjuntos.fields.type")}
                </p>
                <Badge variant="outline" className="mt-1">
                  {archivo.tipo_display}
                </Badge>
              </div>

              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  {t("archivosAdjuntos.fields.size")}
                </p>
                <p className="mt-1">{archivo.tamaño_legible}</p>
              </div>

              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  {t("archivosAdjuntos.fields.extension")}
                </p>
                <p className="mt-1 uppercase">{archivo.extension || "-"}</p>
              </div>

              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  {t("archivosAdjuntos.fields.date")}
                </p>
                <p className="mt-1">{formatDate(archivo.fecha_subida)}</p>
              </div>
            </div>

            {archivo.descripcion && (
              <>
                <Separator />
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-1">
                    {t("archivosAdjuntos.fields.description")}
                  </p>
                  <p className="text-sm">{archivo.descripcion}</p>
                </div>
              </>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                {t("common.close")}
              </Button>
              <Button onClick={handleDownload}>
                <Download className="mr-2 h-4 w-4" />
                {t("archivosAdjuntos.download")}
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center py-8">
            <p className="text-muted-foreground">{t("archivosAdjuntos.notFound")}</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
